import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";

export interface ColumnDef {
  key: string;
  label: string;
  tooltip?: string;
  lock?: boolean;
}

export interface ColumnSettingsValue {
  order: string[];
  visibility: Record<string, boolean>;
  tooltips: boolean;
}

interface TableColumnSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: ColumnDef[];
  value: ColumnSettingsValue;
  defaultValue: ColumnSettingsValue;
  onApply: (value: ColumnSettingsValue) => void;
}

export default function TableColumnSettings({ open, onOpenChange, columns, value, defaultValue, onApply }: TableColumnSettingsProps) {
  const [local, setLocal] = useState<ColumnSettingsValue>(value);

  useEffect(() => {
    if (open) {
      const mergedOrder = Array.from(new Set([...(value.order || []), ...columns.map(c => c.key)]));
      const mergedVis: Record<string, boolean> = {};
      columns.forEach((c) => { mergedVis[c.key] = value.visibility?.[c.key] ?? false; });
      setLocal({ order: mergedOrder, visibility: mergedVis, tooltips: !!value.tooltips });
    }
  }, [open, value, columns]);

  const list = useMemo(() => {
    const keys = Array.from(new Set([...(local.order || []), ...columns.map(c => c.key)]));
    return keys.map(k => columns.find(c => c.key === k)).filter(Boolean) as ColumnDef[];
  }, [local.order, columns]);

  const onDragStart = (e: React.DragEvent<HTMLDivElement>, key: string) => {
    e.dataTransfer.setData("text/plain", key);
  };
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };
  const onDrop = (e: React.DragEvent<HTMLDivElement>, targetKey: string) => {
    e.preventDefault();
    const srcKey = e.dataTransfer.getData("text/plain");
    if (!srcKey || srcKey === targetKey) return;
    setLocal(prev => {
      const order = [...prev.order];
      const from = order.indexOf(srcKey);
      const to = order.indexOf(targetKey);
      if (from === -1 || to === -1) return prev;
      order.splice(to, 0, ...order.splice(from, 1));
      return { ...prev, order };
    });
  };

  const toggleVis = (key: string, checked: boolean) => {
    setLocal(prev => ({ ...prev, visibility: { ...prev.visibility, [key]: checked } }));
  };

  const apply = () => onApply(local);
  const reset = () => setLocal(defaultValue);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Configure Columns</DialogTitle>
        </DialogHeader>

        <div className="space-y-2 max-h-[380px] overflow-auto pr-1">
          {list.map((col) => (
            <div
              key={col.key}
              draggable={!col.lock}
              onDragStart={(e) => onDragStart(e, col.key)}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, col.key)}
              className="flex items-center justify-between rounded-md border bg-card px-3 py-2 gap-3"
            >
              <div className="flex items-center gap-3">
                <span className="cursor-grab select-none text-muted-foreground">⋮⋮</span>
                <span className="text-sm">{col.label}</span>
              </div>
              {!col.lock && (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={!!local.visibility[col.key]} onCheckedChange={(v: any) => toggleVis(col.key, !!v)} />
                  Visible
                </label>
              )}
            </div>
          ))}
        </div>

        <DialogFooter className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={reset}>Reset to Default</Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={apply}>Apply Changes</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


